import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { ITask, TasksColumn } from "src/redux/task/taskReducer";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import {
  computeSelectedRowsTotals,
  buildTotalRowRecord,
  TotalRowRecord,
} from "src/app/methods/computeSelectedRowsTotals";
import TasksTable from "./TasksTable";
import { sortTableData } from "./tasksTableSorting/tableSortingFunctions/sortTableData";
import { sortTitleData } from "./tasksTableSorting/tableSortingFunctions/sortTitleData";
import { IndeterminateCheckbox } from "./IndeterminateCheckbox";

interface TasksTableWrapperInterface {
  columns: TasksColumn[];
}

function computeSelectedRowsTotalRow(
  selectedTasks: ITask[],
  columns: TasksColumn[],
  projectCurrencyCode: string | undefined,
): TotalRowRecord {
  const totals = computeSelectedRowsTotals(
    selectedTasks,
    columns.map((col) => ({
      uuid: col.metaFieldId,
      type: col.metaFieldType as MetaFieldType,
      data: col.data as { currencyCode?: string } | null,
    })),
    { getCurrencyCode: () => projectCurrencyCode },
  );

  return buildTotalRowRecord(totals);
}

export default function TasksTableWrapper({
  columns,
}: TasksTableWrapperInterface) {
  const dictionaryAutoCompletes = useSelector(
    (state: RootState) => state.dictionaryReducer.dictionaryAutoCompletes,
  );

  const membersList = useSelector(
    (state: RootState) => state.projectReducer.activeMembersList,
  );

  const projectCurrencyCode = useSelector(
    (state: RootState) =>
      state.projectReducer.projectBasicData?.data?.currencyCode,
  );

  const projectTasksAutocomplete = useSelector(
    (state: RootState) => state.taskReducer.projectTasksAutocomplete,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );

  const sortingDirection = useSelector(
    (state: RootState) => state.taskFiltersReducer.sortingDirection,
  );

  const selectedTasks = useSelector(
    (state: RootState) => state.taskReducer.selectedTasks,
  );

  const selectedRowsTotalRow = useMemo(
    () =>
      computeSelectedRowsTotalRow(selectedTasks, columns, projectCurrencyCode),
    [selectedTasks, columns, projectCurrencyCode],
  );

  const getVisibleColumns = () => {
    const columnsCopy = [...columns];
    return columnsCopy
      .sort((a, b) => {
        return a.metaFieldRank?.localeCompare(b.metaFieldRank);
      })
      .filter((column) => column.metaFieldVisible)
      .map((column) => {
        return {
          id: column.metaFieldId,
          header: column.metaFieldName,
          footer: column.metaFieldName,
          accessorKey: column.metaFieldId,
          size: 180,
          columnFromApi: column,
          // @ts-ignore
          sortingFn: (...sortingProps) =>
            sortTableData({
              sortingProps,
              sortingDirection,
              membersList,
              tasksAutocomplete: projectTasksAutocomplete,
              dictionaryAutoCompletes,
            }),
        };
      });
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: "drag-handle",
        accessorKey: "drag-handle",
        header: "",
        size: 48,
      },
      {
        id: "select",
        header: ({ table }: any) => (
          <IndeterminateCheckbox
            {...{
              table,
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row, table }: any) => (
          <IndeterminateCheckbox
            {...{
              row,
              table,
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
            }}
          />
        ),
        size: 48,
      },
      {
        id: taskType,
        header: taskType,
        footer: taskType,
        accessorKey: taskType,
        size: 240,
        sortingFn: sortTitleData,
      },
      ...getVisibleColumns(),
      {
        id: " action",
        header: " action",
        footer: " action",
        size: 120,
      },
    ],
    [
      columns,
      projectTasksAutocomplete,
      dictionaryAutoCompletes,
      sortingDirection,
    ],
  );

  return (
    <TasksTable columns={COLUMNS} selectedRowsTotalRow={selectedRowsTotalRow} />
  );
}
